import React, { useState, useEffect } from 'react';
import { 
  Layers, 
  ArrowRightLeft, 
  Wallet, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  ExternalLink,
  Info,
  Zap,
  Shield,
  TrendingUp,
  History
} from 'lucide-react';

interface BridgeTransaction {
  id: number;
  from_chain: string;
  to_chain: string;
  from_address: string;
  to_address: string;
  token: string;
  amount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  tx_hash_from?: string;
  tx_hash_to?: string;
  timestamp: string;
  estimated_time: number;
  fee: number;
}

interface ChainInfo {
  id: string;
  name: string;
  symbol: string;
  rpc_url: string;
  explorer_url: string;
  logo: string;
  supported_tokens: string[];
}

const BridgePage: React.FC = () => {
  const [fromChain, setFromChain] = useState('voltron');
  const [toChain, setToChain] = useState('ethereum');
  const [selectedToken, setSelectedToken] = useState('VTTN');
  const [amount, setAmount] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [balance, setBalance] = useState(0);
  const [bridgeTransactions, setBridgeTransactions] = useState<BridgeTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [estimatedFee, setEstimatedFee] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [activeTab, setActiveTab] = useState('bridge');

  const chains: ChainInfo[] = [
    {
      id: 'voltron',
      name: 'Voltron Mainnet',
      symbol: 'VTTN',
      rpc_url: 'https://rpc.voltron.network',
      explorer_url: 'https://scan.voltron.network',
      logo: '⚡',
      supported_tokens: ['VTTN', 'USDT', 'WETH']
    },
    {
      id: 'ethereum',
      name: 'Ethereum',
      symbol: 'ETH',
      rpc_url: 'https://mainnet.infura.io/v3/...',
      explorer_url: 'https://etherscan.io',
      logo: '🔷',
      supported_tokens: ['VTTN', 'USDT', 'WETH', 'ETH']
    },
    {
      id: 'bsc',
      name: 'BNB Smart Chain',
      symbol: 'BNB',
      rpc_url: 'https://bsc-dataseed.binance.org',
      explorer_url: 'https://bscscan.com',
      logo: '🟡',
      supported_tokens: ['VTTN', 'USDT', 'WETH', 'BNB']
    },
    {
      id: 'polygon',
      name: 'Polygon',
      symbol: 'MATIC',
      rpc_url: 'https://polygon-rpc.com',
      explorer_url: 'https://polygonscan.com',
      logo: '🟣',
      supported_tokens: ['VTTN', 'USDT', 'WETH', 'MATIC']
    }
  ];

  useEffect(() => {
    checkWalletConnection();
    fetchBridgeTransactions();
  }, []);

  useEffect(() => {
    if (amount && fromChain && toChain && selectedToken) {
      calculateFeeAndTime();
    }
  }, [amount, fromChain, toChain, selectedToken]);

  const checkWalletConnection = () => {
    const savedAddress = localStorage.getItem('walletAddress');
    if (savedAddress) {
      setWalletAddress(savedAddress);
      setToAddress(savedAddress); // 기본값으로 설정
      fetchBalance(savedAddress);
    }
  };

  const fetchBalance = async (address: string) => {
    try {
      const response = await fetch(`http://localhost:3600/api/wallet/balance/${address}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // 선택된 토큰에 따라 잔액 설정
          switch (selectedToken) {
            case 'VTTN':
              setBalance(data.data.vttn_balance || 0);
              break;
            case 'USDT':
              setBalance(data.data.usdt_balance || 0);
              break;
            default:
              setBalance(0);
          }
        }
      }
    } catch (error) {
      console.error('잔액 조회 실패:', error);
    }
  };

  const fetchBridgeTransactions = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/bridge/transactions/${walletAddress}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBridgeTransactions(data.data);
        }
      }
    } catch (error) {
      console.error('브릿지 트랜잭션 조회 실패:', error);
      // 더미 데이터
      setBridgeTransactions([
        {
          id: 1,
          from_chain: 'voltron',
          to_chain: 'ethereum',
          from_address: '0x1234...5678',
          to_address: '0x1234...5678',
          token: 'VTTN',
          amount: 1000,
          status: 'completed',
          tx_hash_from: '0xabc123...',
          tx_hash_to: '0xdef456...',
          timestamp: '2024-01-15T10:30:00Z',
          estimated_time: 15,
          fee: 5.5
        },
        {
          id: 2,
          from_chain: 'ethereum',
          to_chain: 'voltron',
          from_address: '0x1234...5678',
          to_address: '0x1234...5678',
          token: 'USDT',
          amount: 500,
          status: 'processing',
          tx_hash_from: '0x789xyz...',
          timestamp: '2024-01-16T14:20:00Z',
          estimated_time: 20,
          fee: 12.3
        }
      ]);
    }
  };

  const calculateFeeAndTime = async () => {
    try {
      const response = await fetch('http://localhost:3600/api/bridge/estimate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_chain: fromChain,
          to_chain: toChain,
          token: selectedToken,
          amount: parseFloat(amount)
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEstimatedFee(data.data.fee);
          setEstimatedTime(data.data.estimated_time);
        }
      }
    } catch (error) {
      console.error('수수료 계산 실패:', error);
      // 기본값 설정
      const baseAmount = parseFloat(amount) || 0;
      setEstimatedFee(baseAmount * 0.005 + 2); // 0.5% + 고정 수수료
      setEstimatedTime(fromChain === 'voltron' ? 10 : 15); // 분 단위
    }
  };

  const handleBridge = async () => {
    if (!walletAddress) {
      alert('지갑을 먼저 연결해주세요.');
      return;
    }

    if (!amount || !toAddress) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    if (parseFloat(amount) > balance) {
      alert('잔액이 부족합니다.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('http://localhost:3600/api/bridge/transfer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_chain: fromChain,
          to_chain: toChain,
          from_address: walletAddress,
          to_address: toAddress,
          token: selectedToken,
          amount: parseFloat(amount)
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('브릿지 전송이 시작되었습니다!');
        setAmount('');
        await fetchBridgeTransactions();
        await fetchBalance(walletAddress);
      } else {
        alert(data.error || '브릿지 전송에 실패했습니다.');
      }
    } catch (error) {
      console.error('브릿지 전송 실패:', error);
      alert('전송 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const swapChains = () => {
    const temp = fromChain;
    setFromChain(toChain);
    setToChain(temp);
  };

  const getChainInfo = (chainId: string) => {
    return chains.find(chain => chain.id === chainId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'processing': return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'failed': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return '완료';
      case 'processing': return '처리중';
      case 'pending': return '대기중';
      case 'failed': return '실패';
      default: return status;
    }
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!walletAddress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <Layers className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">크로스체인 브릿지</h2>
            <p className="text-gray-600 mb-6">
              지갑을 연결하여 브릿지 기능을 사용하세요.
            </p>
            <button
              onClick={() => window.location.href = '/wallet'}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              지갑 연결하기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <Layers className="w-8 h-8 text-orange-500" />
              <span>크로스체인 브릿지</span>
            </h1>
            <p className="text-gray-600 mt-1">
              다양한 블록체인 네트워크 간 자산을 안전하게 이동하세요
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-gray-600">24시간 브릿지 볼륨</div>
              <div className="text-xl font-bold text-orange-600">$2.4M</div>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'bridge', name: '브릿지', icon: Layers },
              { id: 'history', name: '거래 내역', icon: History }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'bridge' && (
            <div className="max-w-2xl mx-auto">
              {/* 브릿지 폼 */}
              <div className="space-y-6">
                {/* From Chain */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From
                  </label>
                  <div className="relative">
                    <select
                      value={fromChain}
                      onChange={(e) => setFromChain(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none"
                    >
                      {chains.map((chain) => (
                        <option key={chain.id} value={chain.id}>
                          {chain.logo} {chain.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    잔액: {formatAmount(balance)} {selectedToken}
                  </div>
                </div>

                {/* 스왑 버튼 */}
                <div className="flex justify-center">
                  <button
                    onClick={swapChains}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  >
                    <ArrowRightLeft className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* To Chain */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To
                  </label>
                  <select
                    value={toChain}
                    onChange={(e) => setToChain(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    {chains.filter(chain => chain.id !== fromChain).map((chain) => (
                      <option key={chain.id} value={chain.id}>
                        {chain.logo} {chain.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 토큰 및 수량 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      토큰
                    </label>
                    <select
                      value={selectedToken}
                      onChange={(e) => {
                        setSelectedToken(e.target.value);
                        if (walletAddress) fetchBalance(walletAddress);
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      {getChainInfo(fromChain)?.supported_tokens.map((token) => (
                        <option key={token} value={token}>{token}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      수량
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      step="0.000001"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* 받는 주소 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    받는 주소
                  </label>
                  <input
                    type="text"
                    value={toAddress}
                    onChange={(e) => setToAddress(e.target.value)}
                    placeholder="0x..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* 수수료 및 예상 시간 */}
                {amount && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">거래 정보</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">전송 수량:</span>
                        <span className="font-medium">{formatAmount(parseFloat(amount))} {selectedToken}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">브릿지 수수료:</span>
                        <span className="font-medium text-orange-600">{formatAmount(estimatedFee)} {selectedToken}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">받을 수량:</span>
                        <span className="font-medium text-green-600">
                          {formatAmount(parseFloat(amount) - estimatedFee)} {selectedToken}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">예상 시간:</span>
                        <span className="font-medium">{estimatedTime}분</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 브릿지 버튼 */}
                <button
                  onClick={handleBridge}
                  disabled={loading || !amount || !toAddress || parseFloat(amount) > balance}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>처리 중...</span>
                    </>
                  ) : (
                    <>
                      <Layers className="w-4 h-4" />
                      <span>브릿지 시작</span>
                    </>
                  )}
                </button>

                {/* 안전성 정보 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">안전한 브릿지</h4>
                      <p className="text-sm text-blue-700">
                        모든 브릿지 거래는 다중 서명과 타임락으로 보호됩니다. 
                        거래는 되돌릴 수 없으니 주소를 다시 한 번 확인해주세요.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">브릿지 거래 내역</h3>
              <div className="space-y-3">
                {bridgeTransactions.length > 0 ? (
                  bridgeTransactions.map((tx) => {
                    const fromChainInfo = getChainInfo(tx.from_chain);
                    const toChainInfo = getChainInfo(tx.to_chain);
                    
                    return (
                      <div key={tx.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(tx.status)}
                            <div>
                              <div className="font-medium text-gray-900">
                                {fromChainInfo?.logo} {fromChainInfo?.name} → {toChainInfo?.logo} {toChainInfo?.name}
                              </div>
                              <div className="text-sm text-gray-600">
                                {new Date(tx.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-gray-900">
                              {formatAmount(tx.amount)} {tx.token}
                            </div>
                            <div className="text-sm text-gray-600">
                              수수료: {formatAmount(tx.fee)} {tx.token}
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-gray-600 mb-1">From</div>
                            <div className="font-mono text-gray-900">
                              {formatAddress(tx.from_address)}
                            </div>
                            {tx.tx_hash_from && (
                              <a
                                href={`${fromChainInfo?.explorer_url}/tx/${tx.tx_hash_from}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-orange-600 hover:text-orange-700 flex items-center space-x-1 mt-1"
                              >
                                <span>트랜잭션 보기</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          <div>
                            <div className="text-gray-600 mb-1">To</div>
                            <div className="font-mono text-gray-900">
                              {formatAddress(tx.to_address)}
                            </div>
                            {tx.tx_hash_to && (
                              <a
                                href={`${toChainInfo?.explorer_url}/tx/${tx.tx_hash_to}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-orange-600 hover:text-orange-700 flex items-center space-x-1 mt-1"
                              >
                                <span>트랜잭션 보기</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3 flex items-center justify-between">
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                            tx.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {getStatusText(tx.status)}
                          </div>
                          {tx.status === 'processing' && (
                            <div className="text-sm text-gray-600">
                              예상 완료: {tx.estimated_time}분
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    브릿지 거래 내역이 없습니다.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BridgePage;